export const SNOMED_SYSTEM = "http://snomed.info/sct" as const;

export enum CanvasAppointmentTypeEnum {
  Telehealth = "telehealth",
  PhoneCall = "phone_call",
}

export type CanvasNoteType = {
  coding: { system: string; code: string; display: string };
  modality: CanvasAppointmentTypeEnum;
  durationMinutes?: number;
};

// Canvas's FHIR `appointmentType` is the scheduleable note type.
// Codes must match the note types configured in Canvas admin. Python plugins
// read theirs from apps/canvas_plugins/shared-lib/constants.
export const CANVAS_NOTE_TYPES = {
  telehealth: {
    coding: { system: SNOMED_SYSTEM, code: "448337001", display: "Telehealth" },
    modality: CanvasAppointmentTypeEnum.Telehealth,
  },
  phoneCall: {
    coding: { system: SNOMED_SYSTEM, code: "185317003", display: "Phone Call" },
    modality: CanvasAppointmentTypeEnum.PhoneCall,
  },
  telehealthTriage: {
    coding: {
      system: "INTERNAL",
      code: "telehealth-triage",
      display: "Telehealth - Triage",
    },
    modality: CanvasAppointmentTypeEnum.Telehealth,
  },

  virtualEvaluation: {
    coding: {
      system: "INTERNAL",
      code: "virtual_evaluation",
      display: "Virtual Evaluation",
    },
    modality: CanvasAppointmentTypeEnum.Telehealth,
    durationMinutes: 45,
  },
  ptFollowUp: {
    coding: {
      system: "INTERNAL",
      code: "pt_follow_up",
      display: "PT Follow-Up",
    },
    modality: CanvasAppointmentTypeEnum.Telehealth,
    durationMinutes: 30,
  },
  medicalConsult: {
    coding: {
      system: "INTERNAL",
      code: "medical_consult",
      display: "Medical Consult",
    },
    modality: CanvasAppointmentTypeEnum.PhoneCall,
    durationMinutes: 15,
  },
  ptCheckIn: {
    coding: { system: "INTERNAL", code: "pt_check_in", display: "PT Check-In" },
    modality: CanvasAppointmentTypeEnum.PhoneCall,
    durationMinutes: 15,
  },
  phoneEvaluation: {
    coding: {
      system: "INTERNAL",
      code: "phone_evaluation",
      display: "Phone Evaluation",
    },
    modality: CanvasAppointmentTypeEnum.PhoneCall,
    durationMinutes: 15,
  },
  phoneConsult: {
    coding: {
      system: "INTERNAL",
      code: "phone_consult",
      display: "Phone Consult",
    },
    modality: CanvasAppointmentTypeEnum.PhoneCall,
    durationMinutes: 15,
  },
} as const satisfies Record<string, CanvasNoteType>;

export const EVALUATION_FLOW_NOTE_TYPES = [
  CANVAS_NOTE_TYPES.virtualEvaluation,
  CANVAS_NOTE_TYPES.ptFollowUp,
  CANVAS_NOTE_TYPES.medicalConsult,
  CANVAS_NOTE_TYPES.ptCheckIn,
  CANVAS_NOTE_TYPES.phoneEvaluation,
  CANVAS_NOTE_TYPES.phoneConsult,
] as const satisfies readonly (CanvasNoteType & { durationMinutes: number })[];

export type EvaluationFlowNoteTypeCode =
  (typeof EVALUATION_FLOW_NOTE_TYPES)[number]["coding"]["code"];

export const EVALUATION_FLOW_NOTE_TYPE_CODES: readonly EvaluationFlowNoteTypeCode[] =
  EVALUATION_FLOW_NOTE_TYPES.map((noteType) => noteType.coding.code);

export const isEvaluationFlowNoteType = (
  code: string | undefined
): code is EvaluationFlowNoteTypeCode =>
  EVALUATION_FLOW_NOTE_TYPE_CODES.some(
    (evaluationCode) => evaluationCode === code
  );

// Ongoing care stays with the same PT; virtual evaluations may move.
const SAME_PROVIDER_NOTE_TYPE_CODES: readonly string[] = [
  CANVAS_NOTE_TYPES.ptFollowUp.coding.code,
  CANVAS_NOTE_TYPES.ptCheckIn.coding.code,
];

export const isSameProviderNoteType = (code: string | undefined): boolean =>
  SAME_PROVIDER_NOTE_TYPE_CODES.includes(code ?? "");

const VIDEO_SESSION_NOTE_TYPE_CODES: readonly string[] = [
  CANVAS_NOTE_TYPES.virtualEvaluation.coding.code,
  CANVAS_NOTE_TYPES.ptFollowUp.coding.code,
];

export const isVideoSessionNoteType = (
  code: string | null | undefined
): boolean => VIDEO_SESSION_NOTE_TYPE_CODES.includes(code ?? "");

// `system|code`, the format Canvas's FHIR `/Slot?appointment-type=` filter expects.
export const noteTypeSearchParam = ({ coding }: CanvasNoteType): string =>
  `${coding.system}|${coding.code}`;

export type CanvasNoteTypeCoding = {
  system: string;
  code: string;
  display: string;
};

// Note type a patient's own PROM submissions are filed under. Not
// scheduleable, so it stays out of CANVAS_NOTE_TYPES.
export const PROMS_NOTE_TYPE: CanvasNoteTypeCoding = {
  system: "INTERNAL",
  code: "proms",
  display: "PROMs",
};
